import React, { useEffect, useState } from "react";
import Card from "components/Card/Card";
import {
  Text,
  Flex,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Button,
  Textarea,
  SimpleGrid,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useColorMode,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import apiUrl from "../../../variables/api";

const initialValues = {
    commandName: "",
    title: "",
    description: "",
    commandDescription: "", // Dodane pole
};

const validate = (values) => {
    const errors = {};

    if (!values.commandName) {
        errors.commandName = "Musisz podać nazwę komendy";
    }

    if (!values.title) {
        errors.title = "Musisz podać tytuł";
    }

    if (!values.description) {
        errors.description = "Musisz podać opis";
    }

    if (!values.commandDescription) {
        errors.commandDescription = "Musisz podać opis komendy"; // Walidacja dla nowego pola
    }

    return errors;
};

function Page() {
  const { colorMode } = useColorMode();
  const [data, setData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const fetchData = async () => {
      try {
          const response = await axios.get(`${apiUrl}/data/commands`, {
              headers: { 'x-access-token': localStorage.getItem('token') }
          });
          setData(response.data);
      } catch (error) {
          toast.error("Błąd podczas pobierania danych");
      }
  };

  useEffect(() => {
      fetchData();
  }, []);

  const handleDelete = async (id) => {
      try {
          await axios.delete(`${apiUrl}/data/commands/${id}`, {
              headers: { 'x-access-token': localStorage.getItem('token') }
          });
          setData(data.filter((command) => command.id !== id));
          toast.success("Komenda została usunięta");
      } catch (error) {
          toast.error("Błąd podczas usuwania komendy");
      }
  };

  const handleEdit = (command) => {
      setSelectedCommand(command);
      setIsOpen(true);
  };

  const handleAdd = () => {
      setSelectedCommand(null);
      setIsOpen(true);
  };

  const handlePreview = (command) => {
      setSelectedCommand(command);
      setIsPreviewOpen(true);
  };

  const handleSubmit = async (values) => {
      try {
          if (selectedCommand) {
              await axios.put(`${apiUrl}/data/commands/${selectedCommand.id}`, values, {
                  headers: { 'x-access-token': localStorage.getItem('token') }
              });
              setData(data.map((command) => (command.id === selectedCommand.id ? values : command)));
              toast.success("Komenda została zaktualizowana");
          } else {
              const response = await axios.post(`${apiUrl}/data/commands`, values, {
                  headers: { 'x-access-token': localStorage.getItem('token') }
              });
              setData([...data, response.data]);
              toast.success("Komenda została dodana");
          }
          setIsOpen(false);
      } catch (error) {
          toast.error("Błąd podczas zapisywania komendy");
      }
  };

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Card minH="125px">
        <Button onClick={handleAdd} colorScheme="teal" mb={4}>
          Dodaj nową komendę
        </Button>
        <SimpleGrid columns={{ sm: 1, md: 2, xl: 3 }} spacing="24px">
          {data.map((command) => (
            <Card key={command.id}>
              <Flex direction="column">
                <Text fontWeight="bold">/{command.commandName}</Text>
                <Button mt={2} colorScheme="green" onClick={() => handlePreview(command)}>
                  Podgląd
                </Button>
                <Button mt={2} colorScheme="blue" onClick={() => handleEdit(command)}>
                  Edytuj
                </Button>
                <Button mt={2} colorScheme="red" onClick={() => handleDelete(command.id)}>
                  Usuń
                </Button>
              </Flex>
            </Card>
          ))}
        </SimpleGrid>

        {/* Modal podglądu */}
        <Modal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Podgląd komendy</ModalHeader>
            <ModalBody>
              {selectedCommand && (
                <>
                  <Text fontWeight={"bold"}>Nazwa komendy:</Text>
                    <Text>{selectedCommand.commandName}</Text>
                    <Text fontWeight={"bold"}>Opis komendy:</Text>
                    <Text whiteSpace={"pre"}>{selectedCommand.commandDescription}</Text>
                    <Text fontWeight={"bold"}>Tytuł:</Text>
                    <Text>{selectedCommand.title}</Text>
                    <Text fontWeight={"bold"}>Opis (tresc):</Text>
                    <Text whiteSpace={"pre"}>{selectedCommand.description}</Text>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="red" onClick={() => setIsPreviewOpen(false)}>
                Zamknij
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <AlertDialog isOpen={isOpen} onClose={() => setIsOpen(false)}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {selectedCommand ? "Edytuj komendę" : "Dodaj nową komendę"}
              </AlertDialogHeader>
              <AlertDialogBody>
                <Formik
                  initialValues={selectedCommand || initialValues}
                  validate={validate}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting }) => (
                    <Form>
                      <Field name="commandName">
                        {({ field, form }) => (
                          <FormControl isInvalid={form.errors.commandName && form.touched.commandName}>
                            <FormLabel htmlFor="commandName">Nazwa komendy</FormLabel>
                            <Input {...field} id="commandName" placeholder="Nazwa komendy" />
                            <FormErrorMessage>{form.errors.commandName}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field name="commandDescription">
                        {({ field, form }) => (
                          <FormControl isInvalid={form.errors.commandDescription && form.touched.commandDescription}>
                            <FormLabel htmlFor="commandDescription">Opis komendy</FormLabel> {/* Nowe pole */}
                            <Input {...field} id="commandDescription" placeholder="Opis komendy" />
                            <FormErrorMessage>{form.errors.commandDescription}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field name="title">
                        {({ field, form }) => (
                          <FormControl isInvalid={form.errors.title && form.touched.title}>
                            <FormLabel htmlFor="title">Tytuł embeda</FormLabel>
                            <Input {...field} id="title" placeholder="Tytuł" />
                            <FormErrorMessage>{form.errors.title}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field name="description">
                        {({ field, form }) => (
                          <FormControl isInvalid={form.errors.description && form.touched.description}>
                            <FormLabel htmlFor="description">Opis embeda</FormLabel>
                            <Textarea {...field} id="description" placeholder="Opis" maxH={"500px"}/>
                            <FormErrorMessage>{form.errors.description}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <AlertDialogFooter>
                        <Button onClick={() => setIsOpen(false)}>Anuluj</Button>
                        <Button colorScheme="red" isLoading={isSubmitting} type="submit" ml={3}>
                          Zapisz
                        </Button>
                      </AlertDialogFooter>
                    </Form>
                  )}
                </Formik>
              </AlertDialogBody>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Card>
    </Flex>
  );
}

export default Page;
